import axios from "axios";
import { resolveApiBase } from "./apiBase.js";
import { supabase } from "./supabaseClient.js";
import { applyApiAuthHeaders, isApiRequest, TENANT_ID } from "./authHeaders.js";

export { TENANT_ID, isApiRequest, applyApiAuthHeaders } from "./authHeaders.js";

/** @type {((user: object) => void) | null} */
let onSessionRefreshed = null;

/**
 * App.js registers this so a silent token refresh can update React auth state
 * without forcing another trip through /login.
 */
export function registerSessionRefreshHandler(handler) {
  onSessionRefreshed = typeof handler === "function" ? handler : null;
}

export function readStoredToken() {
  if (typeof window === "undefined") return "";
  try {
    return (window.localStorage.getItem("token") || "").trim();
  } catch {
    return "";
  }
}

export function storeSessionToken(token) {
  if (typeof window === "undefined" || !token) return;
  try {
    window.localStorage.setItem("token", token);
  } catch {
    /* private mode */
  }
}

export function clearStoredToken() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem("token");
  } catch {
    /* private mode */
  }
}

/**
 * Exchange a live Supabase session for a Mansion JWT. Used when the stored JWT
 * expired but Google OAuth left a refreshable Supabase session behind.
 */
export async function refreshApiSession(apiBase) {
  if (!supabase) return null;

  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();

  if (error || !session?.access_token) {
    return null;
  }

  const base =
    apiBase ||
    `${resolveApiBase({
      hostname: typeof window !== "undefined" ? window.location.hostname : "",
      configured: process.env.REACT_APP_BACKEND_URL,
    })}/api`;

  const response = await axios.post(
    `${base}/auth/supabase`,
    { access_token: session.access_token },
    {
      withCredentials: true,
      headers: applyApiAuthHeaders({ headers: {} }).headers,
    }
  );

  const token = response.data?.token;
  if (token) {
    storeSessionToken(token);
  }

  if (onSessionRefreshed && response.data) {
    onSessionRefreshed(response.data);
  }

  return response.data;
}

/**
 * Attach auth headers on every API call and silently refresh once on 401.
 * Cross-site session cookies are blocked on app.cheepsweepz.shop, so admin
 * pages must not depend on cookies alone.
 */
export function installAuthAxiosInterceptor(options = {}) {
  const apiBase = options.apiBase;

  axios.interceptors.request.use((config) => {
    if (!isApiRequest(config)) return config;
    return applyApiAuthHeaders(config, readStoredToken());
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error?.config;
      const status = error?.response?.status;

      if (!config || !isApiRequest(config) || status !== 401 || config._authRetried) {
        return Promise.reject(error);
      }

      config._authRetried = true;

      try {
        const refreshed = await refreshApiSession(apiBase);
        if (!refreshed?.token) {
          return Promise.reject(error);
        }
        applyApiAuthHeaders(config, refreshed.token);
        return axios.request(config);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }
  );
}
